import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatTile } from 'components/ui/stat-tile';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table';
import { DataTableToolbar, TableSearch, SegmentedTabs, ColumnsToggle, TableCount } from 'components/ui/data-table';
import Avatar from 'components/ui/avatar';
import { useSearchParamState } from 'hooks/use-search-param-state';
import { useIntegrity } from 'modules/analytics';
import type { RiskAttempt, RiskBand } from 'modules/analytics';
import type { AnalyticsFilters } from '../useAnalyticsFilters';
import { SectionCard, LoadingBlock, ErrorBlock, EmptyBlock, RiskBadge } from './shared';
import { IntegrityEventBar } from './charts';

const breakdownParts = (b: RiskAttempt['breakdown'], labelOf: (k: string) => string): string => {
  const parts: string[] = [];
  if (b.tabSwitch) parts.push(`${labelOf('tab_switch')} ${b.tabSwitch}`);
  if (b.focusLost) parts.push(`${labelOf('focus_lost')} ${b.focusLost}`);
  if (b.copyPaste) parts.push(`${labelOf('copy_paste')} ${b.copyPaste}`);
  if (b.fullscreenExit) parts.push(`${labelOf('fullscreen_exit')} ${b.fullscreenExit}`);
  if (b.screenshot) parts.push(`${labelOf('screenshot')} ${b.screenshot}`);
  return parts.join(' · ') || '—';
};

const IntegrityTab: React.FC<{ orgId?: string; filters: AnalyticsFilters }> = ({ orgId, filters }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useIntegrity({ orgId, params: filters.params });

  // `iq`, not `q`: the People tab owns `q` (see useAnalyticsFilters).
  const { input: search, setInput: setSearch } = useSearchParamState('iq');
  const [bandTab, setBandTab] = useState<'all' | RiskBand>('all');
  const [cols, setCols] = useState({ test: true, avgScore: true, riskScore: true, signals: true });

  // Client-side filter of the (small, capped) top-risk list by band + candidate/test text.
  const filteredRisk = useMemo(() => {
    const riskAttempts = data?.topRiskAttempts ?? [];
    const q = search.trim().toLowerCase();
    return riskAttempts.filter((a) => {
      if (bandTab !== 'all' && a.band !== bandTab) return false;
      if (
        q &&
        !`${a.candidateName ?? ''} ${a.candidateEmail ?? ''} ${a.testTitle ?? ''}`.toLowerCase().includes(q)
      )
        return false;
      return true;
    });
  }, [data?.topRiskAttempts, search, bandTab]);

  if (isError) return <ErrorBlock message={error instanceof Error ? error.message : undefined} />;
  if (isLoading) return <LoadingBlock />;
  if (!data || data.totalAttempts === 0) return <EmptyBlock message={t('analytics_integrity_empty')} />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label={t('analytics_integrity_monitored')}
          value={`${data.monitoredAttempts}/${data.totalAttempts}`}
        />
        <StatTile
          label={t('analytics_integrity_flagged_rate')}
          value={`${data.flaggedRate}%`}
          tone={data.flaggedRate > 0 ? 'error' : undefined}
        />
        <StatTile
          label={t('analytics_risk_high')}
          value={data.riskBands.high}
          tone={data.riskBands.high > 0 ? 'warning' : undefined}
        />
        <StatTile label={t('analytics_risk_low')} value={data.riskBands.low} />
      </div>

      <SectionCard title={t('analytics_integrity_events')} description={t('analytics_integrity_events_hint')}>
        {data.byType.length === 0 ? <EmptyBlock message={t('analytics_integrity_no_events')} /> : <IntegrityEventBar data={data.byType} />}
      </SectionCard>

      <h3 className="text-base font-semibold text-foreground">{t('analytics_integrity_top_risk')}</h3>
      {data.topRiskAttempts.length === 0 ? (
        <EmptyBlock message={t('analytics_integrity_no_flags')} />
      ) : (
        <>
          <DataTableToolbar>
            <TableSearch
              value={search}
              onChange={setSearch}
              placeholder={t('search')}
              aria-label={t('search')}
            />
            <ColumnsToggle
              columns={[
                { id: 'test', label: t('analytics_test'), visible: cols.test },
                { id: 'avgScore', label: t('avg_score'), visible: cols.avgScore },
                { id: 'riskScore', label: t('analytics_risk_score'), visible: cols.riskScore },
                { id: 'signals', label: t('analytics_integrity_signals'), visible: cols.signals },
              ]}
              onChange={(id, visible) => setCols((c) => ({ ...c, [id]: visible }))}
            />
            <SegmentedTabs<'all' | RiskBand>
              aria-label={t('analytics_risk_score')}
              value={bandTab}
              onChange={setBandTab}
              options={[
                { value: 'all', label: t('all') },
                { value: 'high', label: t('analytics_risk_high') },
                { value: 'medium', label: t('analytics_risk_medium') },
                { value: 'low', label: t('analytics_risk_low') },
              ]}
            />
            <TableCount>{filteredRisk.length} {t('items')}</TableCount>
          </DataTableToolbar>
          {filteredRisk.length === 0 ? (
            <EmptyBlock message={t('analytics_integrity_no_flags')} />
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-[720px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('analytics_people_candidate')}</TableHead>
                    {cols.test && <TableHead>{t('analytics_test')}</TableHead>}
                    {cols.avgScore && <TableHead>{t('avg_score')}</TableHead>}
                    {cols.riskScore && <TableHead>{t('analytics_risk_score')}</TableHead>}
                    {cols.signals && <TableHead>{t('analytics_integrity_signals')}</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRisk.map((a: RiskAttempt) => (
                    <TableRow key={a.attemptId}>
                      <TableCell>
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar name={a.candidateName} size="sm" />
                          <div className="min-w-0">
                            <p className="font-medium text-foreground text-sm truncate">{a.candidateName}</p>
                            <p className="text-xs text-muted-foreground truncate">{a.candidateEmail}</p>
                          </div>
                        </div>
                      </TableCell>
                      {cols.test && (
                        <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate" title={a.testTitle}>{a.testTitle}</TableCell>
                      )}
                      {cols.avgScore && (
                        <TableCell className="tabular-nums">{a.score == null ? '—' : `${a.score}%`}</TableCell>
                      )}
                      {cols.riskScore && (
                        <TableCell><RiskBadge band={a.band} score={a.riskScore} /></TableCell>
                      )}
                      {cols.signals && (
                        <TableCell className="text-xs text-muted-foreground">
                          {breakdownParts(a.breakdown, (k) => t(`analytics_event_${k}`))}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default IntegrityTab;
